import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { atlasApi } from '../services/atlasApi';

const ACCESS_GRANTED_KEY = 'atlas_access_granted';
const ACCESS_WALLET_KEY = 'atlas_access_wallet';

// Feature flag to disable invite gate for testing
// Default to disabled until ready for production launch
const INVITE_GATE_DISABLED = import.meta.env.VITE_DISABLE_INVITE_GATE !== 'false';

interface AccessContextType {
  hasAccess: boolean;
  isLoading: boolean;
  error: string | null;
  validateCode: (code: string, walletAddress?: string) => Promise<boolean>;
  checkAccess: (walletAddress: string) => Promise<boolean>;
  clearAccess: () => void;
}

const AccessContext = createContext<AccessContextType | undefined>(undefined);

interface AccessProviderProps {
  children: ReactNode;
}

export function AccessProvider({ children }: AccessProviderProps) {
  const [hasAccess, setHasAccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check localStorage on mount (or bypass if gate disabled)
  useEffect(() => {
    if (INVITE_GATE_DISABLED) {
      console.log('[Access] Invite gate disabled via VITE_DISABLE_INVITE_GATE');
      setHasAccess(true);
      setIsLoading(false);
      return;
    }

    const storedAccess = localStorage.getItem(ACCESS_GRANTED_KEY);
    if (storedAccess === 'true') {
      setHasAccess(true);
    }
    setIsLoading(false);
  }, []);

  const validateCode = useCallback(async (code: string, walletAddress?: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await atlasApi.validateCode(code, walletAddress);

      if (response.valid) {
        localStorage.setItem(ACCESS_GRANTED_KEY, 'true');
        if (walletAddress) {
          localStorage.setItem(ACCESS_WALLET_KEY, walletAddress);
        }
        setHasAccess(true);
        return true;
      } else {
        setError(response.message || 'Invalid invite code');
        return false;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to validate code';
      setError(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const checkAccess = useCallback(async (walletAddress: string): Promise<boolean> => {
    try {
      const response = await atlasApi.checkAccess(walletAddress);

      if (response.hasAccess) {
        localStorage.setItem(ACCESS_GRANTED_KEY, 'true');
        localStorage.setItem(ACCESS_WALLET_KEY, walletAddress);
        setHasAccess(true);
        return true;
      }
      return false;
    } catch (err) {
      console.error('Failed to check access:', err);
      return false;
    }
  }, []);

  const clearAccess = useCallback(() => {
    localStorage.removeItem(ACCESS_GRANTED_KEY);
    localStorage.removeItem(ACCESS_WALLET_KEY);
    setHasAccess(false);
    setError(null);
  }, []);

  return (
    <AccessContext.Provider
      value={{
        hasAccess,
        isLoading,
        error,
        validateCode,
        checkAccess,
        clearAccess,
      }}
    >
      {children}
    </AccessContext.Provider>
  );
}

export function useAccess(): AccessContextType {
  const context = useContext(AccessContext);
  if (context === undefined) {
    throw new Error('useAccess must be used within an AccessProvider');
  }
  return context;
}
